import { CurrencyPipe } from "@angular/common";
import { AfterViewInit, Component, TemplateRef, ViewChild } from "@angular/core";

import { ReactiveFormsModule } from "@angular/forms";
import { Store, createSelector } from "@ngrx/store";

import { isNil } from "lodash";
import pluralize from "pluralize";
import { FreyaCommonModule } from "src/app/freya-common/freya-common.module";
import { LibModule } from "src/app/lib.ts/lib.module";
import { ResponsiveHelperService } from "src/app/services/responsive-helper.service";
import { ArrayMinLenghtDirective } from "src/app/shared/array-min-length.directive";
import { SharedModule } from "src/app/shared/shared.module";

import { brandingFeature } from "src/app/state/branding.store";

import { getZoneActivePrice } from "src/app/utilities/product.util";

import { JobEventSelectComponent } from "../event-select/event-select.component";
import { selectJob } from "../job-tool.reducer";
import { workOrdersSelectors } from "../job-workorders/job-workorders.selectors";
import { JobMutateObjectComponent, Step } from "../mutate-object/mutate-object.component";
import { mutateObjectFeature } from "../mutate-object/mutate-object.feature";

import { JobMutateChargeActions } from "./mutate-charges.actions";

@Component({
    selector: 'app-job-mutate-charges',
    standalone: true,
    imports: [
        SharedModule,
        LibModule,
        ReactiveFormsModule,
        FreyaCommonModule,
        JobMutateObjectComponent,
        JobEventSelectComponent,
        ArrayMinLenghtDirective,
    ],
    templateUrl: './mutate-charges.component.html',
    styleUrl: './mutate-charges.component.scss',
})

export class JobMutateChargesComponent implements AfterViewInit {

    @ViewChild('product') productRef: TemplateRef<any>;
    @ViewChild('description') descriptionRef: TemplateRef<any>;
    @ViewChild('amount') amountRef: TemplateRef<any>;
    @ViewChild('quantity') quantityRef: TemplateRef<any>;
    @ViewChild('event') eventRef: TemplateRef<any>;
    @ViewChild('taxes') taxesRef: TemplateRef<any>;
    @ViewChild('expenses') expensesRef: TemplateRef<any>;

    public templates: Record<string, TemplateRef<any>>;

    public actions = JobMutateChargeActions;

    public viewModel$ = this.store.select(createSelector(
      mutateObjectFeature.selectMutateObjectDialog,
      selectJob,
      workOrdersSelectors.selectProductsSuggestionsForCustomCharges,
      brandingFeature.selectBranding,
      brandingFeature.selectTaxes,
      brandingFeature.selectExpenses,
      mutateObjectFeature.selectMutateObjectDialog,
      (
          { mutators },
          job,
          products,
          { zoneId },
          taxOptions,
          expenseOptions,
          { mutateType },
      ) => {

          const currency = job?.currency || 'USD';

          const {
              productName,
              description,
              amount,
              quantity,
              taxes,
              expenses: selectedExpenses,
              productId,
              eventIds,
          } = mutators.charge;

          const selectedProduct = products.find((p) => p.id === productId);

          const activePrice = getZoneActivePrice(selectedProduct?.prices || [], zoneId);

          const amountDisabled = Boolean(selectedProduct);

          const unit = selectedProduct?.unitLabel || 'unit';

          const productUnitLabel = ' ' + pluralize(unit, quantity);

          const expenses = selectedExpenses.map((e) => ({ ...e, total: e.unitCost * (quantity || 0) }));

          const expenseTotal = expenses.reduce((acc, expense) => acc + expense.unitCost, 0);

          const steps: Step[] = [
              {
                  name: 'Product',
                  templateId: 'product',
                  editable: mutateType === 'create',
                  reviewValue: productName,
                  invalid: mutateType === 'create' && !productName?.trim(),
              },
              {
                  name: 'Description',
                  templateId: 'description',
                  editable: true,
                  reviewValue: description,
              },
              {
                  name: 'Amount',
                  templateId: 'amount',
                  editable: mutateType === 'create',
                  reviewValue: amount && this.currencyPipe.transform(amount, currency),
                  invalid: isNil(amount),
              },
              {
                  name: 'Quantity',
                  templateId: 'quantity',
                  editable: !selectedProduct && mutateType === 'create',
                  reviewValue: quantity && quantity + productUnitLabel,
                  invalid: isNil(quantity),
              },
              {
                  name: 'Event',
                  templateId: 'event',
                  editable: mutateType === 'create',
                  reviewValue: eventIds.length.toFixed(),
                  invalid: !eventIds.length,
              },
              {
                  name: 'Taxes',
                  templateId: 'taxes',
                  editable: mutateType === 'create',
                  reviewValue: taxes.length.toFixed(),
              },
              {
                  name: 'Subtotal',
                  editable: false,
                  reviewValue: amount && quantity && this.currencyPipe.transform(amount * quantity, currency),
              },
              {
                  name: 'Costs',
                  editable: mutateType === 'create',
                  templateId: 'expenses',
                  reviewValue: `${expenses.length} (${this.currencyPipe.transform(expenseTotal, currency)})`,
                  invalid: expenses.some((e) => !e.expenseId || !e.unitCost),
              },
          ];

          const productSuggestions = productName ?
              products.filter((p) => p.name.toLowerCase().indexOf(productName.toLowerCase()) >= 0) :
              products;

          return {
              currency,
              productName,
              description,
              amount,
              quantity,
              eventIds,
              taxes,
              expenses,
              taxOptions,
              productSuggestions,
              expenseOptions,
              steps,
              productUnitLabel,
              amountDisabled,
              isPercentCharge: activePrice?.priceType === 'percentage',
          }
        },
    ));

    ngAfterViewInit(): void {
        this.templates = {
            product: this.productRef,
            description: this.descriptionRef,
            amount: this.amountRef,
            quantity: this.quantityRef,
            event: this.eventRef,
            taxes: this.taxesRef,
            expenses: this.expensesRef,
        };
    }

    constructor(
        public store: Store,
        public responsiveHelper: ResponsiveHelperService,
        private currencyPipe: CurrencyPipe,
    ) { }

    public trackByIndex(index: number) {
        return index;
    }

}
